import { createPortal } from 'react-dom'
import DismissButton from 'components/dismiss-button'
import styles from 'css/components/lesson-builder/student-logins-overlay.module.css'
import { Class } from '@all-for-one/shared-types'
import navLogo from 'img/logo-main.png'

interface StudentLoginsOverlayProps {
    classData: Class
    onDismiss: () => void
}

export const StudentLoginsOverlay = ({ classData, onDismiss }: StudentLoginsOverlayProps) => {
    const students = classData?.students || []
    const classPassword = (classData?.defaultPassword || 'afo').trim()

    // The browser names a printed/saved PDF after the page title, which is why the
    // same list came out as the lesson name from one screen and "All for One -
    // Classrooms" from the other. Retitle for the duration of the print only.
    const printFileName = () => {
        const name = (classData?.name || 'Class')
            .trim()
            .replace(/[^\w\s-]/g, '')   // drop punctuation a filesystem dislikes
            .replace(/\s+/g, '_')
        return `Student_Logins_${name || 'Class'}`
    }

    const handlePrint = () => {
        const previousTitle = document.title

        // Restore on afterprint rather than immediately: window.print() blocks in
        // some browsers and returns straight away in others, and restoring too
        // early would put the old title back before the dialog had read it.
        const restore = () => {
            document.title = previousTitle
            document.body.classList.remove('printing-logins')
            window.removeEventListener('afterprint', restore)
        }
        window.addEventListener('afterprint', restore)

        document.title = printFileName()
        document.body.classList.add('printing-logins')
        window.print()
        onDismiss()
    }

    // Mounted on <body> rather than inside the calling page. On screen this is
    // identical — .overlay is already position:fixed — but it makes the printed
    // list a direct child of <body>, so one print rule works from every screen
    // instead of each page needing its own list of things to hide.
    return createPortal(
        <div className="overlay">
            <div className={`overlay-content-container ${styles.container}`}>
                <div className={styles.printHeader}>
                    <img src={navLogo} alt="All for One" className={styles.printLogo} />
                </div>
                <div className="overlay-content-header">
                    Student Logins - {classData?.name}
                    <DismissButton onClick={onDismiss} />
                </div>
                <div className={styles.content}>
                    <div className={styles.passwordSection}>
                        <div className={styles.classNameRow}>
                            <span className={styles.passwordLabel}>Class:</span>
                            <span className={styles.className}>{classData?.name}</span>
                        </div>
                        <div className={styles.passwordRow}>
                            <span className={styles.passwordLabel}>Class Password:</span>
                            <span className={styles.password}>{classPassword}</span>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>User ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student) => (
                                    <tr key={student.studentId}>
                                        <td className={styles.studentName}>{student.studentName?.trim()}</td>
                                        <td className={styles.studentId}>{student.studentId?.trim()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className={styles.footer}>
                    <button className={styles.printButton} onClick={handlePrint}>
                        Print List
                    </button>
                    <div className={styles.hint}>
                        Project or print this screen to help students log in
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}
